import { useQuery, useQueryClient } from '@tanstack/react-query';

import { createQueryKeys, useCustomMutation } from '@lemon/shared';
import { useWebCoreStore } from '@lemon/web-core';

import {
    analyzePouchById,
    analyzePouchByIdPublic,
    createPouch,
    deletePouch,
    fetchPouchById,
    fetchPouches,
    updatePouch,
} from '../api';
import { updateCacheWithDeleted, updateCacheWithNewPouch, updateCacheWithUpdated } from '../utils';

import type { CreatePouchDTO, UpdatePouchDTO } from '../types';
import type { PaginationType, Params } from '@lemon/shared';
import type { PouchView } from '@lemoncloud/epyt-goods-api';

export const pouchKeys = createQueryKeys('pouch');
export const analyzedPouchKeys = createQueryKeys('analyzedPouch');

export const useAnalyzedPouch = (params: Params) => {
    const isAuthenticated = useWebCoreStore(state => state.isAuthenticated);

    return useQuery<any>({
        queryKey: analyzedPouchKeys.list(params ?? {}),
        queryFn: async () => {
            if (!isAuthenticated) {
                return await analyzePouchByIdPublic(params);
            }
            return await analyzePouchById(params);
        },
        refetchOnWindowFocus: false,
        enabled: !!params['id'],
    });
};

export const usePouches = (params: Params) =>
    useQuery<PaginationType<PouchView[]>>({
        queryKey: pouchKeys.list(params ?? {}),
        queryFn: async () => {
            const result = await fetchPouches(params);
            return { ...result, data: result.list || [] } as PaginationType<PouchView[]>;
        },
        refetchOnWindowFocus: false,
    });

export const usePouch = (pouchId: string) =>
    useQuery<PouchView>({
        queryKey: pouchKeys.detail(pouchId),
        queryFn: async () => {
            return await fetchPouchById(pouchId);
        },
        refetchOnWindowFocus: false,
        enabled: !!pouchId,
    });

export const useCreatePouch = () => {
    const queryClient = useQueryClient();

    return useCustomMutation((data: CreatePouchDTO) => createPouch(data), {
        onSuccess: (newPouch: PouchView) => {
            // 캐시 업데이트
            updateCacheWithNewPouch(queryClient, 'pouch', newPouch);
            queryClient.setQueryData(pouchKeys.detail(newPouch.id!), newPouch);
        },
        onError: error => {
            console.error('Failed to create pouch:', error);
        },
    });
};

export const useDeletePouch = () => {
    const queryClient = useQueryClient();

    return useCustomMutation((id: string) => deletePouch(id), {
        onSuccess: (deletedPouch: PouchView, variables: string) => {
            // 캐시 업데이트
            updateCacheWithDeleted(queryClient, 'pouch', variables);
            queryClient.removeQueries({ queryKey: pouchKeys.detail(variables) });
        },
        onError: error => {
            console.error('Failed to delete pouch:', error);
        },
    });
};

export const useUpdatePouch = () => {
    const queryClient = useQueryClient();

    return useCustomMutation((dto: UpdatePouchDTO) => updatePouch(dto), {
        onSuccess: (updatedPouch: PouchView, variables: UpdatePouchDTO) => {
            // 캐시 업데이트
            updateCacheWithUpdated(queryClient, 'pouch', updatedPouch, variables.pouchId);
            queryClient.setQueryData(pouchKeys.detail(variables.pouchId), updatedPouch);
        },
        onError: error => {
            console.error('Failed to update pouch:', error);
        },
    });
};
